import { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '../Icons.jsx';
import { useAuth } from '../../contexts/AuthContext.jsx';
import { useBrand } from '../../contexts/BrandContext.jsx';
import { api } from '../../services/api/client.js';
import { missingBrandFields } from '../../lib/activationProgress.js';
import { FREE_ONBOARDING_PATH } from '../../lib/freeOnboarding.js';
import { useFreePreview } from '../../contexts/FreePreviewContext.jsx';
import { useEmailVerificationActions } from '../../lib/useEmailVerificationActions.js';

const RESEARCH_STAGE = { queued: 'stCollect', running: 'stCollect', gathering: 'stCollect', analyzing: 'stCompetitors', synthesizing: 'stSynthesize', verifying: 'stVerify' };

// Observe the server-created first post, never get-or-create or fill extra
// drafts. The persisted content ID is the receipt, including after reload or
// on another device; no expiring browser journey marker is required.
export default function FirstPostExperience({ resumeSetup = true }) {
  const brand = useBrand();
  const { user } = useAuth() || {};
  const preview = useFreePreview();
  const scope = `${user?.id}:${user?.organizationId}:${brand.activeBrandId}:${preview.mode}`;
  return preview.mode === 'verify'
    ? <VerificationNotice key={scope} user={user} preview={preview} />
    : <FirstPostSession key={scope} brand={brand} user={user} resumeSetup={resumeSetup} />;
}

function VerificationNotice({ user, preview }) {
  const { t } = useTranslation();
  const { resend, sending, sent, error } = useEmailVerificationActions();
  useEffect(() => {
    const refresh = () => preview.refresh?.();
    window.addEventListener('focus', refresh);
    return () => window.removeEventListener('focus', refresh);
  }, [preview.refresh]);
  return <section className="card sj-done first-post-setup">
    <span className="sj-ok"><Icon name="mail" size={14} /></span>
    <div className="sj-tx">
      <div className="sj-tt">{t('freePreview.verifyTitle')}</div>
      <div className="sj-ts">{t('firstPostOnboarding.verifyBody', { email: user?.email || '' })}</div>
      {error && <p role="alert">{error}</p>}
    </div>
    <button type="button" className="btn btn-secondary btn-sm" disabled={sending || sent} onClick={resend}>
      {t(sent ? 'freePreview.verifySent' : 'freePreview.verifyResend')}
    </button>
  </section>;
}

function FirstPostSession({ brand, user, resumeSetup }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const preview = useFreePreview();
  const [state, setState] = useState({ loading: true });
  const [revision, setRevision] = useState(0);

  useEffect(() => {
    // An empty brand list is legitimate for a newly verified account. Read
    // the profile so its 404 can resume the interview; a present profile
    // still requires an exact active-brand match below.
    if (!user?.id || (brand.enabled && brand.loading)) return;
    let cancelled = false;
    let timer;
    const started = Date.now();
    const read = async () => {
      try {
        const response = await api.profile.getBrandProfile().catch(error => {
          if (error?.status !== 404) throw error;
          return null;
        });
        if (cancelled) return;
        if (!response) {
          setState(preview.mode !== 'active' || preview.readOnly || preview.drafts?.ready > 0 ? { error: true } : { incomplete: true });
          return;
        }
        const data = response?.data ?? response;
        const profile = data?.brandProfile ?? data;
        if (!profile?.id || (brand.enabled && profile.id !== brand.activeBrandId)) throw new Error('Unknown brand');
        // Bounded lookup: later manual posts must not displace the receipt,
        // but a broken/repeating cursor must never keep this read alive.
        let cursor;
        let found;
        const cursors = new Set();
        let pages = 0;
        do {
          const response = await api.content.list({ limit: 20, brandProfileId: profile.id, ...(cursor ? { cursor } : {}) });
          if (cancelled) return;
          const page = response?.data ?? response;
          if (!Array.isArray(page?.content)) throw new Error('Unknown content');
          found = page.content.find(row => {
            const item = row?.contentItem;
            return item?.id && item.source === 'first_post'
              && item.userId === user.id && item.organizationId === user.organizationId && item.brandProfileId === profile.id;
          });
          cursor = page.nextCursor;
          if (!found && cursor) {
            if (typeof cursor !== 'string' || cursors.has(cursor) || ++pages >= 10) throw new Error('Content lookup incomplete');
            cursors.add(cursor);
          }
        } while (!found && cursor && !cancelled);
        if (cancelled) return;
        if (found) {
          const item = found.contentItem;
          if (item.archivedAt || ['deleted', 'archived'].includes(item.status)) throw new Error('First post unavailable');
          const image = found.activeVersion?.imageUrl || found.mediaPreview?.url || found.mediaUrls?.[0];
          if (item.generationStatus === 'failed' || found.jobVisualStatus === 'failed') throw new Error('Generation failed');
          const caption = found.activeVersion?.body || found.activeVersion?.caption || item.body;
          if (['ready', 'draft', 'scheduled', 'published'].includes(item.status) && image && caption?.trim()
            && !['queued', 'processing', 'generating', 'visualizing', 'pending'].includes(found.jobVisualStatus)) {
            setState({ postId: item.id });
            return;
          }
        } else if (preview.drafts?.ready > 0 || preview.readOnly) {
          // The server reports an existing result, but it is no longer in
          // the owned content list. Do not restart the grant/interview.
          throw new Error('First post unavailable');
        } else if (missingBrandFields(profile).length && preview.mode === 'active') {
          setState({ incomplete: true });
          return;
        }
        if (Date.now() - started >= 10 * 60 * 1000) throw new Error('First post not available yet');
        // These scoped GETs only describe observed stages. Missing/failed
        // reads stay unknown; they never trigger a fallback generation.
        const [researchRead, strategyRead] = await Promise.allSettled([
          api.research.list({ brandProfileId: profile.id }),
          api.strategy.get({ brandProfileId: profile.id }),
        ]);
        if (cancelled) return;
        const researchData = researchRead.status === 'fulfilled' ? researchRead.value?.data ?? researchRead.value : null;
        const research = researchData?.reports?.find(report => !report.brandProfileId || report.brandProfileId === profile.id);
        const strategyData = strategyRead.status === 'fulfilled' ? strategyRead.value?.data ?? strategyRead.value : null;
        const strategy = strategyData?.strategy;
        const hasStrategy = Boolean(strategy && (!strategy.brandProfileId || strategy.brandProfileId === profile.id));
        if (research?.status === 'failed' && !hasStrategy && !found) throw new Error('Research failed');
        const postRunning = found && (['queued', 'processing', 'generating', 'visualizing', 'pending'].includes(found.jobVisualStatus)
          || ['queued', 'processing', 'generating'].includes(found.contentItem.generationStatus));
        setState({ waiting: true, research: research?.status, hasStrategy, postRunning: Boolean(postRunning) });
        timer = setTimeout(() => { api.invalidateCache?.(['/api/content', '/api/research', '/api/strategy']); read(); }, 15000);
      } catch (error) {
        if (!cancelled) setState({ error: true });
      }
    };
    read();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [user?.id, user?.organizationId, brand.enabled, brand.loading, brand.activeBrandId, preview.mode, preview.readOnly, preview.drafts?.ready, revision]);

  const retry = () => {
    api.invalidateCache?.(['/api/content', '/api/brand-profile', '/api/research', '/api/strategy']);
    setState({ loading: true });
    setRevision(value => value + 1);
  };
  if (state.incomplete && resumeSetup) return <Navigate to={FREE_ONBOARDING_PATH} replace />;
  const steps = [
    ['journey.stepResearch', state.research === 'ready' ? 'finishSetup.statusDone' : state.research === 'failed' ? 'journey.stFailed' : RESEARCH_STAGE[state.research] ? `research.${RESEARCH_STAGE[state.research]}` : 'firstPostOnboarding.statusUnknown'],
    ['journey.stepStrategy', state.hasStrategy ? 'finishSetup.statusDone' : 'firstPostOnboarding.statusUnknown'],
    ['journey.stepFirstPost', state.postRunning ? 'journey.stDrafting' : 'firstPostOnboarding.statusUnknown'],
  ];
  return <section className={`card sj-done first-post-setup${state.loading || state.waiting ? ' first-post-preparing' : ''}`} aria-busy={Boolean(state.loading || state.waiting)}>
    <span className="sj-ok"><Icon name={state.postId ? 'check' : 'sparkles'} size={14} /></span>
    <div className="sj-tx" role={state.error ? 'alert' : 'status'} aria-live="polite">
      <div className="sj-tt">{t(state.postId ? 'journey.doneTitle' : state.error ? 'freePublishing.loadError' : state.incomplete ? 'freePreview.noBrandTitle' : 'firstPostOnboarding.preparingTitle')}</div>
      {state.postId && <div className="sj-ts">{t('firstPostOnboarding.carriedBody')}</div>}
      {state.incomplete && <div className="sj-ts">{t('firstPostOnboarding.included')}</div>}
      {state.loading && <div className="sj-ts">{t('common.loading')}</div>}
      {state.waiting && <ul className="first-post-stages">{steps.map(([label, status]) => <li key={label} data-state={status === 'finishSetup.statusDone' ? 'done' : status === 'firstPostOnboarding.statusUnknown' ? 'unknown' : status === 'journey.stFailed' ? 'failed' : 'active'}>
        <span>{t(label)}</span><span>{t(status)}</span>
      </li>)}</ul>}
      {(state.loading || state.waiting) && <div className="sj-ts first-post-reassure">{t('firstPostOnboarding.continueWorking')}</div>}
    </div>
    {state.postId && <button type="button" className="btn btn-primary sj-cta" onClick={() => navigate(`/app/reveal/${encodeURIComponent(state.postId)}`)}>
      {t('journey.doneCta')} <Icon name="arrowRight" size={13} />
    </button>}
    {state.error && <button type="button" className="btn btn-secondary btn-sm" onClick={retry}>{t('common.retry')}</button>}
    {state.incomplete && <Link to={FREE_ONBOARDING_PATH} className="btn btn-secondary btn-sm">{t('onboardingNudge.continueCta')}</Link>}
    <Link to="/app/library" className="btn btn-ghost btn-sm">{t('nav.library')}</Link>
  </section>;
}
